import { useCallback, useEffect, useState } from "react";
import { Typography, Box, Stack } from "@mui/material";
import { CustomCheckbox } from "../../CustomCheckbox";
import { AccountItem, AccountsData, Contact } from "types";
import { ContactItem } from "./ContactItem";
import { AccountsItem } from "./AccountsItem";
import { CustomAccordion } from "../../CustomAccordion";
import { useElementSize } from "hooks/useElementSize";
import { useVirtualizedList } from "hooks/useVirtualizedList";

type Props = {
  accounts: AccountsData;
  contacts: Contact[];
  setSelectedList: (selected: Record<string, string[]>) => void;
  title: string;
  expandAccordion: string;
  setExpandAccordion: (title: string) => void;
};

const ITEM_HEIGHT = 42;
const VIEWPORT_HEIGHT = 320;

export const MismatchAccordion = ({
  accounts,
  contacts,
  setSelectedList,
  title,
  expandAccordion,
  setExpandAccordion,
}: Props) => {
  const [shouldExpand, setShouldExpand] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);

  // Groups for accounts
  const accountGroups = [
    { label: "Created", key: "created", items: accounts.created },
    { label: "Updated", key: "updated", items: accounts.updated },
    { label: "Deleted", key: "deleted", items: accounts.deleted },
  ];

  const getGroupedSelectedItems = () => {
    const grouped: Record<string, string[]> = {
      created: [],
      updated: [],
      deleted: [],
      contacts: [],
    };

    accounts.created.forEach((item) => {
      if (selected.includes(item.mismatch_id))
        grouped.created.push(item.mismatch_id);
    });
    accounts.updated.forEach((item) => {
      if (selected.includes(item.mismatch_id))
        grouped.updated.push(item.mismatch_id);
    });
    accounts.deleted.forEach((item) => {
      if (selected.includes(item.mismatch_id))
        grouped.deleted.push(item.mismatch_id);
    });
    contacts.forEach((item) => {
      if (selected.includes(item.account_id))
        grouped.contacts.push(item.account_id);
    });

    return grouped;
  };

  useEffect(() => {
    setSelectedList(getGroupedSelectedItems());
  }, [selected]);

  const getAllAccountIds = () =>
    Object.values(accounts)
      .flat()
      .map((a) => a.mismatch_id);

  const getAllContactIds = () => contacts.map((c) => c.account_id);

  const getAllIds = () => [...getAllAccountIds(), ...getAllContactIds()];

  const toggleAll = () => {
    const all = getAllIds();
    const checked = all.filter((id) => selected.includes(id));
    const isAllChecked = checked.length === all.length;
    setSelected(
      isAllChecked
        ? selected.filter((id) => !all.includes(id))
        : [...selected, ...all.filter((id) => !selected.includes(id))],
    );
  };

  const toggleGroup = (ids: string[]) => {
    const checked = ids.filter((id) => selected.includes(id));
    const isAllChecked = checked.length === ids.length;
    setSelected(
      isAllChecked
        ? selected.filter((id) => !ids.includes(id))
        : [...selected, ...ids.filter((id) => !selected.includes(id))],
    );
  };

  const toggleSingle = useCallback((id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((el) => el !== id) : [...prev, id],
    );
  }, []);

  const [accountsRef, { height: accountsHeight }] =
    useElementSize<HTMLDivElement>();
  const [contactsRef, { height: contactsHeight }] =
    useElementSize<HTMLDivElement>();

  // Virtualization for contacts
  const {
    visibleItems: visibleContacts,
    containerHeight: contactsContainerHeight,
    startIndex: contactsStartIndex,
    handleScroll: handleContactsScroll,
  } = useVirtualizedList<Contact>({
    items: contacts,
    itemHeight: ITEM_HEIGHT,
    viewportHeight: contactsHeight,
  });

  const renderAccountsAccordion = () => {
    const all = getAllAccountIds();
    const checkedCount = all.filter((id) => selected.includes(id)).length;
    if (all.length === 0) return null;

    return (
      <CustomAccordion
        expandHandler={() => setShouldExpand(true)}
        header={
          <>
            <CustomCheckbox
              checked={checkedCount === all.length}
              indeterminate={checkedCount > 0 && checkedCount < all.length}
              onClick={(e) => {
                e.stopPropagation();
                toggleGroup(all);
              }}
            />
            <Typography variant="subtitle2" color="#1F293299">
              Accounts ({checkedCount} of {all.length})
            </Typography>
          </>
        }
      >
        {accountGroups.map(({ label, items }) => {
          if (!items.length) return null;

          const {
            visibleItems: visibleAccounts,
            containerHeight: accountsContainerHeight,
            startIndex: accountsStartIndex,
            handleScroll: handleAccountsScroll,
          } = useVirtualizedList<AccountItem>({
            items,
            itemHeight: ITEM_HEIGHT,
            viewportHeight: accountsHeight,
          });

          return (
            <Box paddingLeft={"14px"}>
              <Box key={label}>
                <Typography variant="subtitle2" color="#1F293299">
                  {label}
                </Typography>
                <Box
                  ref={accountsRef}
                  onScroll={() => {
                    if (accountsRef.current) {
                      handleAccountsScroll(accountsRef.current.scrollTop);
                    }
                  }}
                  sx={{
                    maxHeight: VIEWPORT_HEIGHT,
                    overflowY: "auto",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      height: `${accountsContainerHeight}px`,
                      position: "relative",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: `${accountsStartIndex * ITEM_HEIGHT}px`,
                        left: 0,
                        right: 0,
                      }}
                    >
                      {visibleAccounts.map((item) => (
                        <AccountsItem
                          key={item.mismatch_id}
                          id={item.mismatch_id}
                          name={item.account_name}
                          isSelected={selected.includes(item.mismatch_id)}
                          toggleSingle={toggleSingle}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </CustomAccordion>
    );
  };

  const renderContactsAccordion = () => {
    const ids = getAllContactIds();
    const checkedCount = ids.filter((id) => selected.includes(id)).length;

    if (ids.length === 0) return null;

    return (
      <CustomAccordion
        expandHandler={() => setShouldExpand(true)}
        header={
          <>
            <CustomCheckbox
              checked={checkedCount === ids.length}
              indeterminate={checkedCount > 0 && checkedCount < ids.length}
              onClick={(e) => {
                e.stopPropagation();
                toggleGroup(ids);
              }}
            />
            <Typography variant="subtitle2" color="#1F293299">
              Contacts ({checkedCount} of {ids.length})
            </Typography>
          </>
        }
      >
        <Box
          ref={contactsRef}
          onScroll={() => {
            if (contactsRef.current) {
              handleContactsScroll(contactsRef.current.scrollTop);
            }
          }}
          sx={{
            maxHeight: VIEWPORT_HEIGHT,
            overflowY: "auto",
            position: "relative",
            pl: "14px",
          }}
        >
          <Box
            sx={{
              height: `${contactsContainerHeight}px`,
              position: "relative",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: `${contactsStartIndex * ITEM_HEIGHT}px`,
                left: 0,
                right: 0,
              }}
            >
              {visibleContacts.map((contact) => (
                <ContactItem
                  key={contact.account_id}
                  contact={contact}
                  isSelected={selected.includes(contact.account_id)}
                  toggleSingle={toggleSingle}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </CustomAccordion>
    );
  };

  const allIds = getAllIds();
  const topCheckedCount = allIds.filter((id) => selected.includes(id)).length;

  useEffect(() => {
    if (allIds.length !== 0) setExpandAccordion(title);
  }, allIds);

  if (allIds.length === 0) return null;

  return (
    <CustomAccordion
      shouldExpand={shouldExpand}
      expandHandler={() => setExpandAccordion(title)}
      expand={expandAccordion === title}
      header={
        <Stack
          flexDirection="row"
          alignItems="center"
          onClick={(e) => {
            e.stopPropagation();
            toggleAll();
          }}
        >
          <CustomCheckbox
            checked={topCheckedCount === allIds.length}
            indeterminate={
              topCheckedCount > 0 && topCheckedCount < allIds.length
            }
          />
          <Typography fontWeight={500} color="#1F2932">
            {title}
            <span style={{ color: "#1F293299" }}>
              ({topCheckedCount} of {allIds.length})
            </span>
          </Typography>
        </Stack>
      }
    >
      <Stack paddingLeft="28px">
        {renderAccountsAccordion()}
        {renderContactsAccordion()}
      </Stack>
    </CustomAccordion>
  );
};
